import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState, useCallback, useContext } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS, { BASE_URL } from "../../../constants/apiConstants";
import { useNavigate } from "react-router-dom";
import { BiX, BiTrash } from "react-icons/bi";
import "../css/RoomModal.css";
import AuthContext from "../../../context/AuthProvider";
import { useTranslation } from "react-i18next";

export default function RoomModal({ open, onClose, modalName, data }) {
  const axiosPrivate = useAxiosPrivate();
  const [room, setRoom] = useState("");
  const [roomImage, setRoomImage] = useState(null);
  const [capacity, setCapacity] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const { school } = useContext(AuthContext);
  const { t } = useTranslation();
  const isEdit = modalName.toLowerCase().includes("edit");

  const resetForm = useCallback(() => {
    setRoom("");
    setRoomImage(null);
    setCapacity("");
    setErrors({});
  }, []);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageChange(e.dataTransfer.files[0]);
    }
  };

  const handleImageChange = (file) => {
    if (!file) return;

    if (!file.type.match("image.*")) {
      setErrors((prev) => ({ ...prev, image: t("room_modal.imageTypeError") }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        image: t("room_modal.imageSizeError"),
      }));
      return;
    }

    setRoomImage(file);
    setErrors((prev) => ({ ...prev, image: undefined }));
  };

  const resetUpload = () => {
    setRoomImage(null);
    setErrors((prev) => ({ ...prev, image: undefined }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!room.trim()) newErrors.room = t("room_modal.roomNameRequired");
    if (!capacity) newErrors.capacity = t("room_modal.capacityRequired");
    if (isNaN(capacity) || capacity < 1)
      newErrors.capacity = t("room_modal.capacityInvalid");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("name", room.trim());
      if (roomImage instanceof File) formData.append("image", roomImage);
      formData.append("capacity", capacity);
      formData.append("nursery", school.id);

      const url = isEdit
        ? API_URLS.getRoomDetail(school.id, data.id)
        : API_URLS.getSchoolRooms(school.id);
      const method = isEdit ? "put" : "post";

      await axiosPrivate[method](url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      handleClose();
      navigate(0);
    } catch (error) {
      console.error("Request error:", error);
      setErrors((prev) => ({
        ...prev,
        server:
          error.response?.data?.message ||
          t("room_modal.genericError"),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      if (isEdit && data) {
        setRoom(data.room || "");
        setCapacity(data.capacity || "");
        setRoomImage(data.image || null);
      } else {
        resetForm();
      }
    }
  }, [open, isEdit, data, resetForm]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modalStyle">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography variant="h5" fontWeight="bold" color="secondary">
            {t("room_modal.roomTitle", {title: t(`actions.${modalName}`)})}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <BiX />
          </IconButton>
        </Box>

        <Box mb={3}>
          <Typography
            variant="body1"
            component="label"
            display="block"
            color="secondary"
            gutterBottom
          >
            {t("room_modal.roomNameLabel")} <span className="text-danger">*</span>
          </Typography>
          <input
            type="text"
            value={room}
            className={`form-control ${errors.room ? "is-invalid" : ""}`}
            onChange={(e) => {
              setRoom(e.target.value);
              setErrors((prev) => ({ ...prev, room: undefined }));
            }}
            placeholder={t("room_modal.roomNamePlaceholder")}
            disabled={isLoading}
          />
          {errors.room && (
            <Typography className="errorText">{errors.room}</Typography>
          )}
        </Box>

        <Box mb={3}>
          <Typography
            variant="body1"
            component="label"
            display="block"
            color="secondary"
            gutterBottom
          >
            {t("room_modal.capacityLabel")} <span className="text-danger">*</span>
          </Typography>
          <input
            type="number"
            value={capacity}
            className={`form-control ${errors.capacity ? "is-invalid" : ""}`}
            onChange={(e) => {
              setCapacity(e.target.value);
              setErrors((prev) => ({ ...prev, capacity: undefined }));
            }}
            placeholder={t("room_modal.capacityPlaceholder")}
            min="1"
            disabled={isLoading}
          />
          {errors.capacity && (
            <Typography className="errorText">{errors.capacity}</Typography>
          )}
        </Box>

        <Box mb={4}>
          <Typography
            variant="body1"
            component="label"
            display="block"
            color="secondary"
            gutterBottom
          >
            {t("room_modal.roomPicture")}
          </Typography>
          {roomImage ? (
            <Box className="imagePreviewContainer">
              <Box display="flex" alignItems="center" gap={4}>
                <Box width={100} height={100} flexShrink={0}>
                  <img
                    src={
                      typeof roomImage === "string"
                        ? BASE_URL + roomImage
                        : URL.createObjectURL(roomImage)
                    }
                    alt="Room preview"
                    className="imagePreview"
                  />
                </Box>
                <Box flexGrow={1}>
                  <Typography variant="body2">
                    <i className="bi bi-file-image textPrimary"></i>
                    {typeof roomImage === "string"
                      ? t("room_modal.currentImage")
                      : roomImage.name}
                  </Typography>
                  {typeof roomImage !== "string" && (
                    <Typography color="secondary">
                      {Math.round(roomImage.size / 1024)} KB
                    </Typography>
                  )}
                  <Box mt={1}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<BiTrash />}
                      onClick={resetUpload}
                      disabled={isLoading}
                    >
                      {typeof roomImage === "string"
                        ? t("room_modal.changeImage")
                        : t("room_modal.remove")}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              <div
                className={`dropzone ${dragActive ? "dropzoneActive" : ""}`}
                onClick={() =>
                  document.getElementById("room-image-upload").click()
                }
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Typography variant="body2" color="secondary" mt={1}>
                  <Box component="span" color="primary.main" fontWeight="bold">{t("room_modal.chooseOrDrag")} </Box> {t("room_modal.orDragDrop")}
                </Typography>
                <Typography>
                  {t("room_modal.uploadHint")}
                </Typography>
              </div>
              <input
                type="file"
                id="room-image-upload"
                className="visuallyHiddenInput"
                onChange={(e) =>
                  e.target.files[0] && handleImageChange(e.target.files[0])
                }
                accept="image/*"
              />
              {errors.image && (
                <Typography className="errorText" mt={1} display="block">
                  {errors.image}
                </Typography>
              )}
            </>
          )}
        </Box>

        <Typography color="secondary" mb={4}>
          <strong>{t("room_modal.Note")}:</strong> {t("room_modal.note")}
        </Typography>

        {errors.server && (
          <Typography className="errorText" mb={2}>
            {errors.server}
          </Typography>
        )}

        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={
              isLoading ? <CircularProgress size={20} color="inherit" /> : null
            }
            className="submitButton"
          >
            {isLoading ? t("room_modal.processing") : t(`actions.${modalName}`)}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
